export function normalizeHex(hex?: string | null): string | null {
  if (!hex) return null;

  const value = hex.toString().trim();
  if (!value) return null;

  const normalized = `#${value.replace(/^#/, "")}`;
  if (!/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(normalized)) return null;

  if (normalized.length === 4) {
    const r = normalized[1];
    const g = normalized[2];
    const b = normalized[3];
    return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
  }

  return normalized.toUpperCase();
}

export interface CondensePaletteOptions {
  fallbackColors?: string[];
  limit?: number;
}

export function condensePalette(
  fullColorPalette: (string | null | undefined)[],
  options: CondensePaletteOptions = {},
): string[] {
  const { fallbackColors = [], limit = 5 } = options;
  const paletteEntries = fullColorPalette
    .flat()
    .map((value) => normalizeHex(value))
    .filter((value): value is string => Boolean(value));

  if (paletteEntries.length === 0) {
    return fallbackPalette(fallbackColors, limit);
  }

  const counts = new Map<string, number>();
  const firstIndex = new Map<string, number>();

  paletteEntries.forEach((hex, index) => {
    counts.set(hex, (counts.get(hex) ?? 0) + 1);
    if (!firstIndex.has(hex)) {
      firstIndex.set(hex, index);
    }
  });

  return Array.from(new Set(paletteEntries))
    .sort((a, b) => {
      const countDiff = (counts.get(b) ?? 0) - (counts.get(a) ?? 0);
      if (countDiff !== 0) return countDiff;
      return (firstIndex.get(a) ?? 0) - (firstIndex.get(b) ?? 0);
    })
    .slice(0, limit);
}

export interface ContrastPair {
  backgroundColor: string;
  textColor: string;
  contrastRatio: number;
}

export function determineImageContrastColorsPair(params: {
  colorPalette: string[];
  backgroundColor: string;
}): ContrastPair {
  const colorPalette = params.colorPalette
    .map((value) => normalizeHex(value))
    .filter((value): value is string => Boolean(value));
  const normalizedBackground = normalizeHex(params.backgroundColor) ?? colorPalette[0];
  if (!normalizedBackground) {
    return {
      backgroundColor: "#000000",
      textColor: "#FFFFFF",
      contrastRatio: 21,
    };
  }

  const backgroundRGB = hexToRgb(normalizedBackground);
  if (!backgroundRGB) {
    return {
      backgroundColor: normalizedBackground,
      textColor: colorPalette[0] ?? normalizedBackground,
      contrastRatio: 1,
    };
  }

  const bgLuminance = luminance(backgroundRGB);
  const candidates = colorPalette.filter((hex) => hex !== normalizedBackground);

  let bestText: string | null = null;
  let bestContrast = -Infinity;
  let aaBestText: string | null = null;
  let aaBestContrast = 0;

  candidates.forEach((hex) => {
    const rgb = hexToRgb(hex);
    if (!rgb) return;
    const contrast = contrastRatio(bgLuminance, luminance(rgb));

    if (contrast > bestContrast) {
      bestContrast = contrast;
      bestText = hex;
    }

    if (contrast >= 4.5 && contrast > aaBestContrast) {
      aaBestContrast = contrast;
      aaBestText = hex;
    }
  });

  const textColor = aaBestText ?? bestText ?? colorPalette[0] ?? normalizedBackground;
  const textRgb = hexToRgb(textColor);
  const contrast = textRgb ? contrastRatio(bgLuminance, luminance(textRgb)) : 1;

  return {
    backgroundColor: normalizedBackground,
    textColor,
    contrastRatio: Number(contrast.toFixed(2)),
  };
}

function fallbackPalette(colors: string[], limit: number): string[] {
  return colors
    .map((value) => normalizeHex(value))
    .filter((value): value is string => Boolean(value))
    .filter((value, index, array) => array.indexOf(value) === index)
    .slice(0, limit);
}

function hexToRgb(hex: string): [number, number, number] | null {
  const match = /^#([0-9a-fA-F]{6})$/.exec(hex);
  if (!match) return null;
  const value = match[1];
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return [r, g, b];
}

function luminance(rgb: [number, number, number]): number {
  const [r, g, b] = rgb.map((component) => {
    const channel = component / 255;
    return channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);
  }) as [number, number, number];

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrastRatio(l1: number, l2: number): number {
  const [low, high] = [l1, l2].sort((a, b) => a - b);
  return (high + 0.05) / (low + 0.05);
}
